import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { catchError, retry, switchMap, tap } from 'rxjs/operators';

import { FeatureCheckService } from '@plugins/client/feature-check';
import { MultilanguageService } from '@plugins/client/multilanguage/multilanguage.service';
import { CONTENT_LANGUAGE_HEADER } from '@plugins/id-card-lib/constants';
import {
  BackofficeInstanceDto,
  FrontofficeInstanceDto,
} from '@plugins/id-card-lib/instance';
import { SDK } from '@plugins/middleware-sdk';

import { navigateToMissingFeature } from '../guards/missing-feature.guard';

@Injectable({ providedIn: 'root' })
export class DataService {
  private readonly http = inject(HttpClient);
  protected sdk = inject(SDK);
  protected featureCheck = inject(FeatureCheckService);
  public backofficeInstance!: BackofficeInstanceDto;
  public frontofficeInstance!: FrontofficeInstanceDto;
  protected router = inject(Router);
  protected multilanguageService = inject(MultilanguageService);

  fetchFrontofficeInstance(): Observable<FrontofficeInstanceDto> {
    return this.frontofficeInstance
      ? of(this.frontofficeInstance)
      : this.frontofficeApiRequest();
  }

  protected frontofficeApiRequest() {
    return from(this.sdk.getPreferredLanguage()).pipe(
      switchMap((contentLanguage) => {
        const headers = new HttpHeaders().append(
          CONTENT_LANGUAGE_HEADER,
          contentLanguage,
        );
        return this.http
          .get<FrontofficeInstanceDto>('/api/instance/user-instance', {
            headers,
          })
          .pipe(retry({ delay: 3000, count: 2 }));
      }),
      tap((instance) => {
        this.frontofficeInstance = instance;
      }),
    );
  }

  /** For use in redirect guards to avoid multiple API requests */
  retrieveOrFetchBackofficeInstance(): Observable<BackofficeInstanceDto> {
    return this.backofficeInstance
      ? of(this.backofficeInstance)
      : this.fetchBackofficeInstance();
  }

  fetchBackofficeInstance(): Observable<BackofficeInstanceDto> {
    return from(this.sdk.getPreferredLanguage()).pipe(
      switchMap((contentLanguage) => {
        const headers = new HttpHeaders().append(
          CONTENT_LANGUAGE_HEADER,
          contentLanguage,
        );
        return this.http
          .get<BackofficeInstanceDto>('/api/instance/admin-instance', {
            headers,
          })
          .pipe(
            retry({ delay: 3000, count: 2 }),
            tap((instance) => {
              this.backofficeInstance = instance;
              /** Required step, otherwise multilanguage validation will not work! */
              this.multilanguageService.setLimitedLanguages(instance.languages);
            }),
            catchError((err) => {
              void this.router.navigate(['/404'], { state: { error: true } });
              throw err;
            }),
          );
      }),
    );
  }

  updateInstance(
    instance: Partial<BackofficeInstanceDto>,
  ): Observable<BackofficeInstanceDto> {
    return this.http
      .patch<BackofficeInstanceDto>('/api/instance', { instance })
      .pipe(tap((updated) => (this.backofficeInstance = updated)));
  }

  fetchUser<T>(): Observable<T> {
    return this.featureCheck.isFeatureEnabled('userpicker').pipe(
      switchMap((isEnabled) => {
        // Early throw if feature check indicates feature is disabled
        if (!isEnabled) {
          // eslint-disable-next-line @typescript-eslint/only-throw-error
          throw new HttpErrorResponse({
            status: 401,
            statusText: 'Userpicker not enabled',
          });
        }
        return this.http.get<T>('/api/instance/user-data');
      }),
      catchError((err) => {
        void navigateToMissingFeature(this.router);
        throw err;
      }),
    );
  }
}
